import { executionDecision, selectedCommandStatus, validationMissingValues } from "./consolidationExecution.js";
import { earlierVisitTimes, plannedServiceMinutes, timeWarnings } from "./consolidationFieldHighlights.js";
import { durationLabel, workedSeconds } from "./consolidationPresentation.js";
import { paperMismatch } from "./consolidationGeotabGantt.js";
import { fmtTime } from "../components/geotab/geotabFormat.js";

// Horaires surlignés dans le relevé : avertissements de saisie, heures qui précèdent l'arrêt précédent
// et, avec geotabChecks (paperChecks du Gantt), heures saisies loin de Geotab.
const TIME_LABELS = { service_start: "Début du service", service_end: "Fin du service", lunch_end: "Fin du dîner", arrival: "Arrivée", departure: "Départ" };
export function flaggedTimeDetails(sheet, geotabChecks = null) {
  const visits = [...sheet.visits].sort((a, b) => a.actual_sequence - b.actual_sequence);
  const label = key => { const [field, id] = key.split(":"); return `${TIME_LABELS[field]}${id ? ` · ${visits.find(visit => visit.id === id)?.label}` : ""}`; };
  const details = new Map(Object.entries(timeWarnings(sheet)).map(([key, messages]) => [key, messages]));
  geotabChecks?.forEach((check, key) => { if (paperMismatch(geotabChecks, key) != null) details.set(key, [...(details.get(key) || []), `enregistré ${fmtTime(check.geotab + check.gap)} · Geotab ${fmtTime(check.geotab)}.`]); });
  earlierVisitTimes(visits, sheet.timezone).forEach((earlier, index) => {
    for (const field of ["arrival", "departure"]) if (earlier[field]) {
      const key = `${field}:${visits[index].id}`;
      details.set(key, [...(details.get(key) || []), "Heure antérieure à celle de l’arrêt précédent."]);
    }
  });
  return [...details].map(([key, messages]) => `${label(key)} : ${messages.join(" ")}`);
}
export const flaggedTimes = sheet => flaggedTimeDetails(sheet).length;

// Points de la liste et de sa bulle : « Facturer à » compte une seule fois par commande.
export function sheetListMissingValues(sheet) {
  const orders = [...new Map(sheet.visits.flatMap(visit => visit.orders).map(order => [order.id, order])).values()]
    .filter(order => !order.separate_treatment && selectedCommandStatus(order, executionDecision(sheet, order)) !== "excluded");
  return [...validationMissingValues(sheet, (order, field) => order[field] ?? ""),
    ...orders.filter(order => !order.coop || order.coop_valid === false)
      .map(order => `${order.label} · Facturer à ${order.coop ? "invalide" : "manquant"}.`)];
}

// Résumé des relevés d'un chauffeur pour sa ligne de la liste; seules les valeurs enregistrées comptent.
export function sheetListSummary(sheets) {
  const drafts = sheets.filter(sheet => sheet.id && sheet.status === "draft");
  const missing = drafts.reduce((total, sheet) => total + sheetListMissingValues(sheet).length
    + (sheet.quantity_anomalies || []).length, 0);
  const warnings = drafts.reduce((total, sheet) => total + flaggedTimes(sheet), 0);
  const pending = sheets.filter(sheet => sheet.status !== "validated");
  const complete = pending.length > 0 && pending.every(sheet => sheet.id) && missing === 0 && warnings === 0;
  const worked = sheets.map(workedSeconds);
  if (worked.some(seconds => seconds == null)) return { missing, warnings, complete, hours: null };
  const actual = worked.reduce((total, seconds) => total + seconds, 0) / 60;
  const planned = sheets.reduce((total, sheet) => total + plannedServiceMinutes(sheet), 0);
  const gap = Math.round(actual - planned);
  const gapLabel = gap === 0 ? "" : ` (${gap > 0 ? "+" : "−"}${Math.abs(gap) < 60 ? `${Math.abs(gap)} min` : durationLabel(gap)})`;
  return { missing, warnings, complete,
    hours: `${durationLabel(actual)}${Number.isFinite(planned) ? ` · prévu ${durationLabel(planned)}${gapLabel}` : ""}` };
}
